import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Typography,
  LinearProgress,
  Snackbar,
  Box,
  TextField,
  IconButton,
  Autocomplete,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from 'src/scenes/students/administration/helper/const';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { jsPDF } from 'jspdf';
import ImportDiplomaDialog from './importDiplomaDialog';
import ImportZahvalnicaDialog from './importZahvalnicaDialog';
import { latinToCyrillic } from 'src/helper/helperFunction';


const PageWrapper = styled(Box)({ padding: '24px' });

const HeaderPaper = styled(Paper)({
  padding: '24px 28px',
  borderRadius: '16px',
  marginBottom: '20px',
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 4px 20px rgba(21,101,192,0.3)',
  flexWrap: 'wrap',
  gap: '12px',
});

const ActionButton = styled(Button)(({ disabled }) => ({
  backgroundColor: disabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
  backdropFilter: 'blur(8px)',
  color: disabled ? 'rgba(255,255,255,0.4)' : '#fff',
  fontWeight: 700,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '14px',
  padding: '8px 18px',
  border: '1px solid rgba(255,255,255,0.35)',
  '&:hover': {
    backgroundColor: disabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.32)',
  },
}));

const ImportButton = styled(Button)({
  backgroundColor: 'rgba(255,255,255,0.95)',
  color: '#1565c0',
  fontWeight: 700,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '14px',
  padding: '8px 20px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  '&:hover': { backgroundColor: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' },
});

const ZahvalnicaButton = styled(Button)({
  backgroundColor: 'rgba(255,215,0,0.25)',
  backdropFilter: 'blur(8px)',
  color: '#fff',
  fontWeight: 700,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '14px',
  padding: '8px 18px',
  border: '1px solid rgba(255,215,0,0.6)',
  '&:hover': { backgroundColor: 'rgba(255,215,0,0.38)' },
});

const ContentPaper = styled(Paper)({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  padding: '16px',
});

const FilterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  flexWrap: 'wrap',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8faff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
  },
});

const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => String(2026 + i));

const TYPE_BADGES = {
  '1': { label: 'ZZ', fullKey: 'diplomasBySchool.typeLabels.zlatna', bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
  '2': { label: 'PR', fullKey: 'diplomasBySchool.typeLabels.prviRang', bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  '3': { label: 'F', fullKey: 'diplomasBySchool.typeLabels.finalniRang', bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8' },
  '4': { label: 'Z', fullKey: 'diplomasBySchool.typeLabels.zahvalnica', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const DiplomasBySchool = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resultsExist, setResultsExist] = useState(false);
  const [diplomasExist, setDiplomasExist] = useState(false);

  const currentYear = String(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [searchText, setSearchText] = useState('');
  const [filterSchool, setFilterSchool] = useState('All');
  const [filterLocation, setFilterLocation] = useState('All');
  const [filterType, setFilterType] = useState('All');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterTemplateYear, setFilterTemplateYear] = useState('All');
  const [filterTemplateType, setFilterTemplateType] = useState('All');
  const [importYearWarning, setImportYearWarning] = useState(null);

  const [zahvalnicaDialogOpen, setZahvalnicaDialogOpen] = useState(false);
  const [zahvalnicaFile, setZahvalnicaFile] = useState(null);
  const [zahvalnicaTemplate, setZahvalnicaTemplate] = useState(null);
  const [zahvalnicaFilterYear, setZahvalnicaFilterYear] = useState('All');
  const [zahvalnicaYearWarning, setZahvalnicaYearWarning] = useState(null);

  const [zahvalnicaSchools, setZahvalnicaSchools] = useState([]);
  const [zahvalnicaSchoolsLoading, setZahvalnicaSchoolsLoading] = useState(false);

  const [zahvalnicaGenerating, setZahvalnicaGenerating] = useState(false);
  const [diplomaGenerating, setDiplomaGenerating] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchSavedDiplomas(currentYear);
    checkResultsExist(currentYear);
  }, []);

  useEffect(() => {
    fetchSavedDiplomas(selectedYear);
    checkResultsExist(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    if (zahvalnicaDialogOpen) {
      setZahvalnicaSchoolsLoading(true);
      fetch(`${API_BASE_URL}/api/diplomas.php?action=zahvalnicaSchools`)
        .then((r) => r.json())
        .then((data) => setZahvalnicaSchools(Array.isArray(data) ? data : []))
        .catch(() => setZahvalnicaSchools([]))
        .finally(() => setZahvalnicaSchoolsLoading(false));
    }
  }, [zahvalnicaDialogOpen]);

  const showSnack = (msg, severity = 'info') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getTemplateImageUrl = (template) =>
    template?.id
      ? `${API_BASE_URL}/api/diplomaTemplates.php?action=get_image&id=${template.id}`
      : '';

  const diplomaTypeLabels = {
    '1': { fullKey: 'diplomaTemplate.zlatna' },
    '2': { fullKey: 'diplomaTemplate.prviRang' },
    '3': { fullKey: 'diplomaTemplate.finalniRang' },
    '4': { fullKey: 'diplomaTemplate.zahvalnica' },
    'final rang': { fullKey: 'diplomaTemplate.finalniRang' },
  };
  const getTemplateTypeLabel = (type) => {
    const key = String(type || '').trim();
    const typeConfig = diplomaTypeLabels[key];
    return typeConfig ? t(typeConfig.fullKey) : type || t('general.unknown');
  };

  const regularTemplates = templates.filter((t) => String(t.type) !== '1' && String(t.type) !== '4');
  const templateYears = ['All', ...Array.from(new Set(regularTemplates.map((t) => String(t.year))))];
  const templateTypes = ['All', ...Array.from(new Set(regularTemplates.map((t) => getTemplateTypeLabel(t.type))))];

  const typeFilterOptions = React.useMemo(() => {
    const typeKeys = Array.from(new Set(
      rows.flatMap((r) =>
        String(r.templateTypes || '').split(',').map((k) => k.trim()).filter(Boolean)
      )
    ));
    return [
      { value: 'All', label: t('general.all') },
      ...typeKeys.map((key) => {
        const cfg = TYPE_BADGES[key];
        const label = cfg?.fullKey ? t(cfg.fullKey) : key;
        return { value: key, label };
      }),
    ];
  }, [rows, t]);

  const checkYearTypeConflict = async (templateType, year) => {
    if (!templateType || !year || year === 'All') return null;
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/diplomas.php?action=checkYearType&templateType=${encodeURIComponent(templateType)}&year=${encodeURIComponent(year)}`
      );
      return await r.json();
    } catch {
      return null;
    }
  };

  const handleImportTemplateChange = async (template) => {
    setSelectedTemplate(template);
    setImportYearWarning(null);
    if (!template) return;
    if (selectedYear !== 'All' && String(template.year) !== String(selectedYear)) return;
    const conflict = await checkYearTypeConflict(String(template.type), String(template.year));
    if (conflict?.exists) {
      setImportYearWarning({
        year: template.year,
        type: getTemplateTypeLabel(template.type),
        count: conflict.count,
      });
    }
  };

  const handleZahvalnicaTemplateChange = async (template) => {
    setZahvalnicaTemplate(template);
    setZahvalnicaYearWarning(null);
    if (!template) return;
    const conflict = await checkYearTypeConflict(String(template.type), String(template.year));
    if (conflict?.exists) {
      setZahvalnicaYearWarning({ year: template.year, count: conflict.count });
    }
  };

  const fetchTemplates = () => {
    fetch(`${API_BASE_URL}/api/diplomaTemplates.php`)
      .then((r) => r.json())
      .then((data) => setTemplates(data))
      .catch((err) => console.error('Error fetching templates:', err));
  };

  const checkResultsExist = async (year) => {
    try {
      const yearParam = year && year !== 'All' ? `&year=${encodeURIComponent(year)}` : '';
      const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=checkResults${yearParam}`);
      const data = await r.json();
      setResultsExist(data.exists === true);
    } catch (_) { }
  };

  const fetchSavedDiplomas = async (year) => {
    setRows([]);
    setDiplomasExist(false);
    try {
      let grouped = null;
      const yearParam = year && year !== 'All' ? `&year=${encodeURIComponent(year)}` : '';

      try {
        const aggResp = await fetch(`${API_BASE_URL}/api/diplomas.php?action=countsBySchool${yearParam}`);
        if (aggResp.ok) {
          const aggData = await aggResp.json();
          if (Array.isArray(aggData)) {
            if (aggData.length === 0) {
              setRows([]);
              setDiplomasExist(false);
              return [];
            }
            grouped = {};
            aggData.forEach((item) => {
              const schoolId = item.school_id ?? null;
              const key = schoolId ? String(schoolId) : (item.school_name || t('general.unknown'));
              const schoolName = item.school_name || (schoolId ? t('diplomasBySchool.schoolId', { schoolId }) : t('general.unknown'));
              grouped[key] = {
                location: item.location_name || '',
                schoolId,
                schoolName,
                totalDiplomas: parseInt(item.total_diplomas, 10) || 0,
                validDiplomas: parseInt(item.valid_diplomas, 10) || 0,
                templateTypes: item.template_types || '',
              };
            });
          }
        }
      } catch (_) { }

      if (!grouped) {
        const r = await fetch(`${API_BASE_URL}/api/diplomas.php${yearParam ? `?${yearParam.slice(1)}` : ''}`);
        const text = await r.text();
        let data;
        try { data = JSON.parse(text); } catch { return []; }
        if (!Array.isArray(data)) return [];

        grouped = {};
        data.forEach((item) => {
          const schoolId = item.school_id ?? null;
          const schoolName = item.school_name || item.school || (schoolId != null ? t('diplomasBySchool.schoolId', { schoolId }) : t('general.unknown'));
          const locationName = item.location_name || item.location || '';
          if (!grouped[schoolName]) {
            grouped[schoolName] = { location: locationName, schoolId, totalDiplomas: 0, validDiplomas: 0, templateTypes: '' };
          }
          grouped[schoolName].totalDiplomas += 1;
          if (item.pdf_path) grouped[schoolName].validDiplomas += 1;
        });
      }

      const newRows = Object.entries(grouped).map(([key, block]) => ({
        id: key,
        school: block.schoolName,
        schoolId: block.schoolId,
        location: block.location,
        totalDiplomas: block.totalDiplomas.toString(),
        validDiplomas: block.validDiplomas,
        diplomas: '',
        templateTypes: block.templateTypes || '',
      }));

      setRows(newRows);
      setDiplomasExist(newRows.some((r) => r.validDiplomas > 0));
      return newRows;
    } catch (err) {
      console.error('Error fetching saved diplomas:', err);
      return [];
    }
  };

  // Zlatna Zahvalnica (ZZ, type 1)
  const parseZlatnaExcel = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          const normalise = (row) => {
            const out = {};
            for (const [k, v] of Object.entries(row)) {
              out[k.trim()] = typeof v === 'string' ? v.trim() : v;
            }
            return out;
          };

          const schools = jsonData
            .map(normalise)
            .map((row) => ({
              school_id: String(row['ID'] || row['id'] || row['school_id'] || '').trim(),
              school_name: (row['Назив школе'] || row['name'] || row['Name'] || row['school_name'] || '').trim(),
              location: (row['Место'] || row['location'] || row['Location'] || '').trim(),
            }))
            .filter((s) => s.school_id && s.school_name);

          if (schools.length === 0) {
            reject(new Error(t('diplomasBySchool.invalidExcelColumns')));
            return;
          }
          resolve(schools);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const generateSchoolLevelDiplomas = async (dbMap, template) => {
    const templateUrl = `${API_BASE_URL}/api/diplomaTemplates.php?action=get_image&id=${template.id}`;
    let templateImg = null;
    try {
      templateImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = templateUrl;
      });
    } catch (_) { }

    const bitmap = templateImg ? await createImageBitmap(templateImg) : null;
    const templateType = String(template.type);
    const awardValue = templateType === '4' ? 'Zahvalnica' : 'Zlatna';
    const imgAspect = (templateImg?.width || 1024) / (templateImg?.height || 768);
    const a4Aspect = 297 / 210;

    const payload = dbMap.map((entry) => ({
      'Назив школе': entry.school_name || '',
      'school': entry.school_name || '',
      'Место': entry.location || '',
      'location': entry.location || '',
      'Награда': awardValue,
      '_diplomaId': String(entry.diploma_id || '0'),
      '_templateType': templateType,
      '_certId': entry.cert_id || '',
    }));

    const BATCH_SIZE = 50;

    for (let batchStart = 0; batchStart < payload.length; batchStart += BATCH_SIZE) {
      const batch = payload.slice(batchStart, batchStart + BATCH_SIZE);
      const batchDbMap = dbMap.slice(batchStart, batchStart + BATCH_SIZE);

      const worker = new Worker(new URL('../../workers/diplomaWorker.js', import.meta.url));
      const results = await new Promise((resolve) => {
        const handler = (e) => {
          if (e.data?.id === 999) {
            worker.removeEventListener('message', handler);
            resolve(e.data.results);
          }
        };
        worker.addEventListener('message', handler);
        worker.postMessage({
          id: 999,
          students: batch,
          templateBitmap: bitmap,
          templateWidth: templateImg?.width || 1024,
          templateHeight: templateImg?.height || 768,
        });
      });
      worker.terminate();

      for (let i = 0; i < results.length; i++) {
        const res = results[i];
        const entry = batchDbMap[i];

        const pdf = new jsPDF({
          orientation: imgAspect > a4Aspect ? 'landscape' : 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const blob = new Blob([res.buffer], { type: 'image/jpeg' });
        const imgUrl = URL.createObjectURL(blob);
        let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
        if (imgAspect > pageWidth / pageHeight) {
          drawWidth = pageWidth;
          drawHeight = pageWidth / imgAspect;
          offsetY = (pageHeight - drawHeight) / 2;
        } else {
          drawHeight = pageHeight;
          drawWidth = pageHeight * imgAspect;
          offsetX = (pageWidth - drawWidth) / 2;
        }
        pdf.addImage(imgUrl, 'JPEG', offsetX, offsetY, drawWidth, drawHeight);
        URL.revokeObjectURL(imgUrl);
        const pdfBlob = pdf.output('blob');

        const formData = new FormData();
        formData.append('pdf', pdfBlob, `${entry.school_name || awardValue}.pdf`);
        formData.append('schoolId', entry.school_id || '');
        formData.append('award', awardValue);
        formData.append('diplomaId', String(entry.diploma_id || '0'));
        if (templateType === '4') {
          formData.append('isZahvalnica', '1');
        } else {
          formData.append('isZlatna', '1');
        }

        let uploaded = false;
        for (let attempt = 0; attempt < 3 && !uploaded; attempt++) {
          try {
            const uploadRes = await fetch(
              `${API_BASE_URL}/api/diplomas.php?action=uploadPdf`,
              { method: 'POST', body: formData }
            );
            if (uploadRes.ok) uploaded = true;
          } catch (_) {
            await new Promise((r) => setTimeout(r, 500));
          }
        }
        if (!uploaded) console.error('Failed to upload diploma for:', entry.school_name);
      }
    }
  };

  const handleZahvalnicaImport = async ({ tab, template, file }) => {
    if (!template) {
      showSnack(t('diplomasBySchool.selectZlatnaFileAndTemplate'), 'warning');
      return;
    }
    if (tab === 0 && !file) {
      showSnack(t('diplomasBySchool.selectZlatnaFileAndTemplate'), 'warning');
      return;
    }

    setZahvalnicaGenerating(true);
    setIsLoading(true);

    try {
      let schools;

      if (tab === 0) {
        schools = await parseZlatnaExcel(file);
      } else {
        schools = zahvalnicaSchools.map((s) => ({
          school_id: String(s.school_id),
          school_name: s.school_name,
          location: s.location_name || '',
        }));
        if (schools.length === 0) {
          showSnack(t('importZahvalnica.zNoSchoolsTitle'), 'warning');
          setZahvalnicaGenerating(false);
          setIsLoading(false);
          return;
        }
      }

      const isZahvalnica = tab === 1;
      const bulkResp = await fetch(`${API_BASE_URL}/api/diplomas.php?action=bulkCreate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          students: schools,
          templateId: template.id,
          isZlatna: !isZahvalnica,
          isZahvalnica,
        }),
      });
      const bulkResult = await bulkResp.json();

      if (!bulkResult.success) {
        showSnack(
          t('diplomasBySchool.bulkCreateError', { error: bulkResult.error || t('general.unknown') }),
          'error'
        );
        setZahvalnicaGenerating(false);
        setIsLoading(false);
        return;
      }

      const dbMap = bulkResult.data || [];
      if (dbMap.length === 0) {
        showSnack(t('diplomasBySchool.noSchoolFoundForIds'), 'warning');
        setZahvalnicaGenerating(false);
        setIsLoading(false);
        return;
      }

      await generateSchoolLevelDiplomas(dbMap, template);

      const successKey = isZahvalnica
        ? 'diplomasBySchool.zahvalnicaGeneratedSuccess'
        : 'diplomasBySchool.zlatnaGeneratedSuccess';
      showSnack(t(successKey, { count: dbMap.length }), 'success');
      await fetchSavedDiplomas(selectedYear);
    } catch (err) {
      console.error(err);
      showSnack(t('diplomasBySchool.importError', { error: err.message }), 'error');
    } finally {
      setZahvalnicaGenerating(false);
      setIsLoading(false);
      setZahvalnicaDialogOpen(false);
      setZahvalnicaFile(null);
      setZahvalnicaTemplate(null);
      setZahvalnicaFilterYear('All');
      setZahvalnicaYearWarning(null);
    }
  };

  // Regular diploma import (types 2 / 3)
  const processExcelImport = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const classMapping = {
            'I OS': 1, 'II OS': 2, 'III OS': 3, 'IV OS': 4, 'V OS': 5, 'VI OS': 6,
            'VII OS': 7, 'VIII OS': 8, 'I SS': 9, 'II SS': 10, 'III SS': 11, 'IV SS': 12,
          };
          const allJsonData = [];
          workbook.SheetNames.forEach((sheetName) => {
            const classNum = classMapping[sheetName];
            if (!classNum) return;
            const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
            jsonData.forEach((row) => { row['class'] = classNum; });
            allJsonData.push(...jsonData);
          });

          const grouped = {};
          allJsonData.forEach((row) => {
            const id = row['id'] || row['ID'] || '';
            const school = (row['Назив школе'] || row['Назив школе '] || row['Naziv skole'] || row['school'] || 'Unknown').toString().trim();
            const location = (row['Место'] || row['Место '] || row['Mesto'] || row['location'] || '').toString().trim();
            const points = parseFloat(String(row['Поени'] || row['Поени '] || row['Poeni'] || row['points'] || '0').replace(',', '.')) || 0;
            const className = row['class'];

            if (!grouped[className]) grouped[className] = {};
            if (!grouped[className][school]) grouped[className][school] = { location, students: [] };
            grouped[className][school].students.push({
              'id': id.toString(),
              'Ime i prezime': (row['Ime i prezime'] || row['Име и презиме'] || '').toString().trim(),
              'Разред': className.toString(),
              'Назив школе': school,
              'Место': location,
              'Категорија': (row['Категорија'] || row['category'] || '').toString().trim(),
              'Професор': (row['Професор'] || row['professor'] || '').toString().trim(),
              'Језик': (row['Језик'] || row['language'] || '').toString().trim(),
              'Поени': points.toString().replace('.', ','),
              'Награда': (row['Награда'] || row['award'] || '').toString().trim(),
              'class': className,
            });
          });
          resolve({ jsonData: allJsonData, grouped });
        } catch (error) {
          showSnack(t('diplomasBySchool.errorProcessingExcel'), 'error');
          reject(error);
        }
      };
      reader.onerror = (e) => { showSnack(t('diplomasBySchool.excelReadError'), 'error'); reject(e); };
      reader.readAsArrayBuffer(file);
    });

  const handleTemplateSelect = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setDiplomaGenerating(true);

    try {
      const { jsonData, grouped } = await processExcelImport(selectedFile);

      const matchBackendEntry = (row, dbEntries) => {
        const rowId = String(row.id || '').trim();
        if (!rowId) return false;
        return dbEntries.some((entry) => String(entry.student_id).trim() === rowId);
      };

      const buildTableAndZip = async (filteredGrouped, dbMap = []) => {
        const resultRows = {};
        const schoolMap = {};

        for (const className in filteredGrouped) {
          for (const [school, data] of Object.entries(filteredGrouped[className])) {
            if (schoolMap[school]) continue;
            const schoolEntry = dbMap.find((d) => d.student?.school_name === school);
            const schoolId = schoolEntry?.school_id;
            if (!schoolId) continue;
            schoolMap[school] = { schoolId, location: data.location };
          }
        }

        for (const [school, { schoolId, location }] of Object.entries(schoolMap)) {
          let allStudents = [];
          try {
            const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=getBySchool&school_id=${schoolId}`);
            allStudents = await r.json();
          } catch (_) { continue; }

          const headers = ['Разред', 'Ime i prezime', 'Назив школе', 'Место', 'Категорија', 'Професор', 'Језик', 'Поени', 'Награда'];
          const sheetRows = allStudents.map((student) => {
            const dbStudent = dbMap.find((d) => String(d.student_id) === String(student.id));
            return {
              'Разред': String(student.class || ''),
              'Ime i prezime': student.name || '',
              'Назив школе': student.school_name || '',
              'Место': student.location_name || '',
              'Категорија': student.category_name || String(student.category ?? ''),
              'Професор': student.professor || '',
              'Језик': student.language || '',
              'Поени': String(student.point_sum ?? '').replace('.', ','),
              'Награда': dbStudent?.award || '',
            };
          });

          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.aoa_to_sheet([headers, ...sheetRows.map((s) => headers.map((h) => s[h] || ''))]);
          XLSX.utils.book_append_sheet(wb, ws, 'students');
          const fileBlob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' });

          try {
            const fd = new FormData();
            fd.append('xlsx', fileBlob, `${school}.xlsx`);
            fd.append('schoolId', schoolId);
            const sr = await fetch(`${API_BASE_URL}/api/diplomas.php?action=saveResult`, { method: 'POST', body: fd });
            const sj = await sr.json();
            if (sj.success) setResultsExist(true);
          } catch (_) { }

          resultRows[school] = { id: school, school, location, studentResults: school, diplomas: '' };
        }
        return { rows: Object.values(resultRows) };
      };

      let dbMap = [];
      if (selectedTemplate) {
        try {
          const bulkResponse = await fetch(`${API_BASE_URL}/api/diplomas.php?action=bulkCreate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ students: jsonData, templateId: selectedTemplate.id }),
          });
          const bulkResult = JSON.parse(await bulkResponse.text());
          if (bulkResponse.ok && bulkResult.success && Array.isArray(bulkResult.data)) {
            dbMap = bulkResult.data;
          } else {
            showSnack(t('diplomasBySchool.bulkCreateError', { error: bulkResult.error || t('general.unknown') }), 'error');
          }
        } catch (_) { showSnack(t('diplomasBySchool.bulkCreateRequestFailed'), 'error'); }
      }

      let filteredGrouped = grouped;
      if (selectedTemplate) {
        if (dbMap.length === 0) {
          showSnack(t('diplomasBySchool.noMatchingStudentsFound'), 'warning');
          setImportDialogOpen(false); setSelectedTemplate(null); setSelectedFile(null); setDiplomaGenerating(false); setIsLoading(false);
          return;
        }
        filteredGrouped = {};
        for (const className in grouped) {
          for (const [school, data] of Object.entries(grouped[className])) {
            const matched = data.students.filter((s) => matchBackendEntry(s, dbMap));
            if (matched.length > 0) {
              filteredGrouped[className] = filteredGrouped[className] || {};
              filteredGrouped[className][school] = { ...data, students: matched };
            }
          }
        }
      }

      await buildTableAndZip(filteredGrouped, dbMap);

      showSnack(t('diplomasBySchool.importSuccess'), 'success');

      if (selectedTemplate) {
        try {
          await generateDiplomasForAllSchools(jsonData, selectedTemplate, dbMap);
          showSnack(t('diplomasBySchool.diplomasGeneratedSuccess'), 'success');
        } catch (_) {
          showSnack(t('diplomasBySchool.diplomasGenerationFailed'), 'error');
        }
      }

      setImportDialogOpen(false); setSelectedTemplate(null); setSelectedFile(null); setImportYearWarning(null);

      await fetchSavedDiplomas(selectedYear);
      setDiplomaGenerating(false)
      setIsLoading(false);
    } catch (_) {
      showSnack(t('diplomasBySchool.importFailed'), 'error');
      setImportDialogOpen(false); setSelectedTemplate(null); setSelectedFile(null); setImportYearWarning(null); setDiplomaGenerating(false); setIsLoading(false);
    }
  };

  const generateDiplomasForAllSchools = async (jsonData, template, dbMap = []) => {
    const templateUrl = template.id
      ? `${API_BASE_URL}/api/diplomaTemplates.php?action=get_image&id=${template.id}` : '';
    let templateImg = null;
    try {
      templateImg = await new Promise((resolve, reject) => {
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img); img.onerror = reject; img.src = templateUrl;
      });
    } catch (_) { }

    const bitmap = templateImg ? await createImageBitmap(templateImg) : null;
    const grouped = {};

    for (const entry of dbMap) {
      const student = entry.student;
      if (!student) continue;
      const school = student.school_name || `School ${entry.school_id}`;
      const className = String(student.class);
      const awardFilter = String(template.type) === '3'
        ? ['I', 'II', 'III'] : ['Похвала', 'I', 'II', 'III'];
      if (!awardFilter.includes(entry.award)) continue;
      if (!grouped[className]) grouped[className] = {};
      if (!grouped[className][school]) grouped[className][school] = [];
      grouped[className][school].push({
        'Име и презиме': student.name || '',
        'Ime i prezime': student.name || '',
        'Разред': String(student.class || ''),
        'class': String(student.class || ''),
        'Назив школе': student.school_name || '',
        'school': student.school_name || '',
        'Матична школа': student.maticna_skola || '',
        'Место': student.location_name || student.city_name || '',
        'location': student.location_name || student.city_name || '',
        'Награда': entry.award || '',
        'award': entry.award || '',
        'Поени': String(student.point_sum ?? '').replace('.', ','),
        'points': String(student.point_sum ?? '').replace('.', ','),
        '_studentId': entry.student_id,
        '_schoolId': entry.school_id,
        '_diplomaId': entry.diploma_id,
        '_templateType': String(template.type || ''),
        '_certId': entry.cert_id || '',
      });
    }

    const numWorkers = navigator.hardwareConcurrency || 8;
    const workers = Array.from({ length: numWorkers }, () =>
      new Worker(new URL('../../workers/diplomaWorker.js', import.meta.url))
    );
    let requestId = 0;

    for (const className in grouped) {
      for (const [school, winners] of Object.entries(grouped[className])) {
        if (!winners.length) continue;
        const chunks = [];
        for (let i = 0; i < winners.length; i += 100) chunks.push(winners.slice(i, i + 100));

        const chunkResults = await Promise.all(
          chunks.map((chunk, index) => {
            const worker = workers[index % numWorkers];
            const id = requestId++;
            return new Promise((resolve) => {
              const handler = (e) => {
                if (e.data.id === id) { worker.removeEventListener('message', handler); resolve(e.data.results); }
              };
              worker.addEventListener('message', handler);
              worker.postMessage({
                id, students: chunk, templateBitmap: bitmap,
                templateWidth: templateImg?.width || 1024,
                templateHeight: templateImg?.height || 768,
              });
            });
          })
        );

        const allResults = chunkResults.flat();
        const imgAspect = (templateImg?.width || 1024) / (templateImg?.height || 768);
        const a4Aspect = 297 / 210;

        for (const res of allResults) {
          const pdf = new jsPDF({
            orientation: imgAspect > a4Aspect ? 'landscape' : 'portrait',
            unit: 'mm', format: 'a4',
          });
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const blob = new Blob([res.buffer], { type: 'image/jpeg' });
          const imgUrl = URL.createObjectURL(blob);
          let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
          if (imgAspect > pageWidth / pageHeight) {
            drawWidth = pageWidth; drawHeight = pageWidth / imgAspect; offsetY = (pageHeight - drawHeight) / 2;
          } else {
            drawHeight = pageHeight; drawWidth = pageHeight * imgAspect; offsetX = (pageWidth - drawWidth) / 2;
          }
          pdf.addImage(imgUrl, 'JPEG', offsetX, offsetY, drawWidth, drawHeight);
          URL.revokeObjectURL(imgUrl);
          const pdfBlob = pdf.output('blob');

          const winner = winners.find((w) => w['Ime i prezime'] === res.name);
          const studentId = winner?._studentId;
          const schoolId = winner?._schoolId;
          const diplomaId = winner?._diplomaId;

          if (studentId) {
            const fd = new FormData();
            fd.append('pdf', pdfBlob, `${res.name}.pdf`);
            fd.append('studentId', studentId);
            fd.append('schoolId', schoolId || '');
            fd.append('award', res.award || '');
            fd.append('templateId', template.id || '');
            fd.append('diplomaId', diplomaId || '');
            try {
              await fetch(`${API_BASE_URL}/api/diplomas.php?action=uploadPdf`, { method: 'POST', body: fd });
            } catch (_) { }
          }
        }
      }
    }
    workers.forEach((w) => w.terminate());
  };

  const downloadAllDiplomas = async () => {
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=downloadAllDiplomas${yearParam}`);
      if (!r.ok) { showSnack(t('diplomasBySchool.failedDownloadAllDiplomas'), 'error'); return; }
      saveAs(await r.blob(), `all_diplomas_${selectedYear}.zip`);
    } catch { showSnack(t('diplomasBySchool.failedDownloadAllDiplomas'), 'error'); }
  };

  const downloadAllResults = async () => {
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=downloadAllResults${yearParam}`);
      if (!r.ok) { showSnack(t('diplomasBySchool.failedDownloadAllResults'), 'error'); return; }
      saveAs(await r.blob(), `all_results_${selectedYear}.zip`);
    } catch { showSnack(t('diplomasBySchool.failedDownloadAllResults'), 'error'); }
  };

  const downloadZip = async (row) => {
    if (!row.schoolId) { showSnack(t('diplomasBySchool.noSchoolIdFound'), 'error'); return; }
    try {
      const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=downloadResultsBySchool&school_id=${encodeURIComponent(row.schoolId)}`);
      if (!r.ok) { showSnack(t('diplomasBySchool.noResultsFoundForSchool'), 'warning'); return; }
      saveAs(await r.blob(), `${row.school}_results_${selectedYear}.xlsx`);
    } catch { showSnack(t('diplomasBySchool.failedDownloadResults'), 'error'); }
  };

  const downloadDiplomaZip = async (row) => {
    if (!row.schoolId) { showSnack(t('diplomasBySchool.noDiplomaZipAvailable'), 'warning'); return; }
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const r = await fetch(`${API_BASE_URL}/api/diplomas.php?action=downloadZip&school_id=${encodeURIComponent(row.schoolId)}${yearParam}`);
      if (!r.ok) { showSnack(t('diplomasBySchool.failedDownloadDiplomaZip'), 'error'); return; }
      const blob = await r.blob();
      const ab = await blob.arrayBuffer();
      const sig = String.fromCharCode(...new Uint8Array(ab.slice(0, 4)));
      if (!['PK\x03\x04', 'PK\x05\x06', 'PK\x07\x08'].includes(sig)) {
        showSnack(t('diplomasBySchool.invalidZipArchive'), 'error'); return;
      }
      saveAs(new Blob([ab], { type: 'application/zip' }), `${row.school}_diplomas_${selectedYear}.zip`);
    } catch { showSnack(t('diplomasBySchool.failedDownloadDiplomaZip'), 'error'); }
  };

  const schoolOptions = ['All', ...Array.from(new Set(rows.map((r) => r.school)))];
  const locationOptions = ['All', ...Array.from(new Set(rows.map((r) => r.location)))];

  const filteredRows = rows.filter((row) => {
    const q = searchText.toLowerCase().trim();
    const qCyr = latinToCyrillic(q);
    const sl = (row.school || '').toLowerCase();
    const ll = (row.location || '').toLowerCase();
    const matchQuery = !q || sl.includes(q) || sl.includes(qCyr) || ll.includes(q) || ll.includes(qCyr);

    const matchType = filterType === 'All' || (() => {
      const selected = typeFilterOptions.find((o) => o.label === filterType);
      if (!selected || selected.value === 'All') return true;
      const rowTypes = String(row.templateTypes || '').split(',').map((k) => k.trim());
      return rowTypes.includes(selected.value);
    })();

    return matchQuery &&
      (filterSchool === 'All' || row.school === filterSchool) &&
      (filterLocation === 'All' || row.location === filterLocation) &&
      matchType;
  });

  const columns = [
    {
      field: 'school', headerName: t('columns.school'), flex: 2, minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a237e' }}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'location', headerName: t('columns.location'), flex: 1, minWidth: 150,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '14px', color: '#555' }}>{params.value}</Typography>
      ),
    },
    {
      field: 'validDiplomas', headerName: t('columns.diplomas'), width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          icon={<EmojiEventsIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            backgroundColor: params.value > 0 ? '#e8f5e9' : '#f5f5f5',
            color: params.value > 0 ? '#2e7d32' : '#999',
            fontWeight: 700, fontSize: '12px',
            border: params.value > 0 ? '1px solid #a5d6a733' : '1px solid #e0e0e0',
          }}
        />
      ),
    },
    {
      field: 'templateTypes', headerName: t('diplomasBySchool.templateTypes'), width: 180,
      renderCell: (params) => {
        const types = String(params.value || '').split(',').map((typeKey) => typeKey.trim()).filter(Boolean);
        if (types.length === 0) {
          return <Typography sx={{ fontSize: '12px', color: '#bbb' }}>—</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
            {types.map((typeKey) => {
              const cfg = TYPE_BADGES[typeKey] || { label: `T${typeKey}`, fullKey: null, bg: '#f5f5f5', color: '#555', border: '#ddd' };
              const fullLabel = cfg.fullKey ? t(cfg.fullKey) : typeKey;
              return (
                <Box
                  key={typeKey}
                  title={fullLabel}
                  sx={{
                    px: 0.9, py: 0.15,
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 700,
                    lineHeight: '20px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    backgroundColor: cfg.bg,
                    color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    cursor: 'default',
                    userSelect: 'none',
                  }}
                >
                  {cfg.label}
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'studentResults', headerName: t('columns.results'), width: 110,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => downloadZip(params.row)} title={t('diplomasBySchool.downloadResults')}
          sx={{ color: '#1976d2', backgroundColor: '#e3f2fd', borderRadius: '8px', padding: '6px', '&:hover': { backgroundColor: '#bbdefb' } }}>
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: 'diplomas', headerName: t('diplomasBySchool.diplomasZip'), width: 140,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => downloadDiplomaZip(params.row)} title={t('diplomasBySchool.downloadDiplomas')}
          sx={{ color: '#7b1fa2', backgroundColor: '#f3e5f5', borderRadius: '8px', padding: '6px', '&:hover': { backgroundColor: '#e1bee7' } }}>
          <FolderZipIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <PageWrapper>
      {isLoading && <LinearProgress sx={{ borderRadius: '4px', mb: 1 }} />}

      <HeaderPaper elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmojiEventsIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('diplomasBySchool.title')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>{t('diplomasBySchool.subtitle')}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>

          <Autocomplete
            options={YEAR_OPTIONS}
            value={selectedYear}
            onChange={(_, v) => { if (v) setSelectedYear(v); }}
            disableClearable
            size="small"
            sx={{
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiAutocomplete-popupIndicator': { color: '#fff' },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('diplomasBySchool.yearLabel')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <CalendarMonthIcon sx={{ fontSize: 18, color: '#fff', mr: 0.5 }} />,
                }}
                sx={{ '& .MuiInputBase-input': { color: '#fff' } }}
              />
            )}
          />

          <ImportButton startIcon={<UploadFileIcon />} onClick={() => setImportDialogOpen(true)}>
            {t('diplomasBySchool.importExcel')}
          </ImportButton>

          <ZahvalnicaButton startIcon={<StarIcon />} onClick={() => setZahvalnicaDialogOpen(true)}>
            {t('diplomasBySchool.zahvalnice')}
          </ZahvalnicaButton>

          <ActionButton disabled={!resultsExist} startIcon={<FileDownloadIcon />} onClick={downloadAllResults}>
            {t('diplomasBySchool.allResults')}
          </ActionButton>
          <ActionButton disabled={!diplomasExist} startIcon={<DownloadIcon />} onClick={downloadAllDiplomas}>
            {t('diplomasBySchool.allDiplomas')}
          </ActionButton>
        </Box>
      </HeaderPaper>

      <ContentPaper>
        <FilterBar>
          <StyledTextField
            label={t('diplomasBySchool.searchPlaceholder')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
            size="small" sx={{ minWidth: '260px' }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: '#1976d2', mr: 0.5, fontSize: 20 }} /> }}
          />
          <Autocomplete value={filterSchool} onChange={(_, v) => setFilterSchool(v || 'All')} options={schoolOptions}
            getOptionLabel={(o) => o === 'All' ? t('general.all') : o}
            renderInput={(params) => <StyledTextField {...params} label={t('columns.school')} size="small" sx={{ minWidth: '220px' }} />}
          />
          <Autocomplete value={filterLocation} onChange={(_, v) => setFilterLocation(v || 'All')} options={locationOptions}
            getOptionLabel={(o) => o === 'All' ? t('general.all') : o}
            renderInput={(params) => <StyledTextField {...params} label={t('columns.location')} size="small" sx={{ minWidth: '180px' }} />}
          />
          <Autocomplete
            value={filterType}
            onChange={(_, v) => setFilterType(v || 'All')}
            options={typeFilterOptions.map((o) => o.label)}
            getOptionLabel={(o) => o === 'All' ? t('general.all') : o}
            renderInput={(params) => (
              <StyledTextField {...params} label={t('diplomasBySchool.templateTypes')} size="small" sx={{ minWidth: '220px' }} />
            )}
          />
        </FilterBar>

        <DataGrid
          rows={filteredRows} columns={columns} getRowId={(row) => row.id}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 20]} checkboxSelection={false} loading={isLoading}
          components={{ LoadingOverlay }} disableColumnMenu autoHeight
          componentsProps={{
            pagination: {
              labelRowsPerPage: t('pagination.rowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} ${t('pagination.of')} ${count !== -1 ? count : `${t('pagination.moreThan')} ${to}`}`,
            },
          }}
          sx={{
            border: 'none', borderRadius: '12px',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2', color: '#fff', fontSize: '14px',
              fontWeight: 700, borderRadius: '10px 10px 0 0', borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeader': { outline: 'none !important' },
            '& .MuiDataGrid-cell': {
              color: '#1a237e', fontSize: '14px',
              borderBottom: '1px solid #e8edf5',
              display: 'flex', alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#f8faff' },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#e3f2fd',
              borderTop: '1px solid #ddd',
              borderRadius: '0 0 12px 12px',
            },
            '& .MuiDataGrid-sortIcon': { color: '#fff' },
            '& .MuiDataGrid-menuIconButton': { color: '#fff' },
          }}
        />
      </ContentPaper>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ImportDiplomaDialog
        open={importDialogOpen}
        onClose={() => { if (diplomaGenerating) return; setImportDialogOpen(false); setImportDialogOpen(false); setSelectedFile(null); setSelectedTemplate(null); setImportYearWarning(null); }}
        templates={regularTemplates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={handleImportTemplateChange}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        handleImport={handleTemplateSelect}
        filterTemplateYear={filterTemplateYear}
        setFilterTemplateYear={setFilterTemplateYear}
        filterTemplateType={filterTemplateType}
        setFilterTemplateType={setFilterTemplateType}
        templateYears={templateYears}
        templateTypes={templateTypes}
        getTemplateImageUrl={getTemplateImageUrl}
        yearWarning={importYearWarning}
        loading={diplomaGenerating}
      />

      <ImportZahvalnicaDialog
        open={zahvalnicaDialogOpen}
        onClose={() => {
          if (zahvalnicaGenerating) return;
          setZahvalnicaDialogOpen(false);
          setZahvalnicaFile(null);
          setZahvalnicaTemplate(null);
          setZahvalnicaFilterYear('All');
          setZahvalnicaYearWarning(null);
        }}
        templates={templates}
        selectedTemplate={zahvalnicaTemplate}
        setSelectedTemplate={handleZahvalnicaTemplateChange}
        selectedFile={zahvalnicaFile}
        setSelectedFile={setZahvalnicaFile}
        handleImport={handleZahvalnicaImport}
        filterYear={zahvalnicaFilterYear}
        setFilterYear={setZahvalnicaFilterYear}
        getTemplateImageUrl={getTemplateImageUrl}
        yearWarning={zahvalnicaYearWarning}
        zahvalnicaSchools={zahvalnicaSchools}
        zahvalnicaLoading={zahvalnicaSchoolsLoading}
        loading={zahvalnicaGenerating}
      />
    </PageWrapper>
  );
};

export default DiplomasBySchool;